import { useState, useEffect } from 'react';
import { Client, Release, ClientRelease, Status, Environment } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface RawRelease {
  id: string;
  version: string;
  status: Status;
  environment: Environment;
  brd_link: string | null;
  approved_by: string | null;
  notes: string | null;
  created_at: string;
  planned_release_date: string | null;
  client_id: string;
  owner_id: string;
  prework: string | null;
}

export const useDashboardReleases = (clients: Client[]) => {
  const [releases, setReleases] = useState<ClientRelease[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchReleases = async () => {
      if (clients.length === 0) return;
      
      setIsLoading(true);
      try {
        // Fetch all releases
        const { data: releasesData, error } = await supabase
          .from('releases')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching releases:', error);
          toast({
            title: 'Error',
            description: 'Failed to load releases for dashboard',
            variant: 'destructive',
          });
          return;
        }

        // Map of client IDs to client objects for quick lookup
        const clientMap = clients.reduce((map, client) => {
          map[client.id] = client;
          return map;
        }, {} as {[id: string]: Client});

        // Process each release
        const processedReleases = (releasesData as RawRelease[])
          .map((release) => {
            const clientId = release.client_id;
            const client = clientMap[clientId];
            
            if (!client) return null; // Skip if client not found
            
            const processedRelease: Release = {
              id: release.id,
              version: release.version,
              status: release.status,
              environment: release.environment,
              brdLink: release.brd_link || '',
              approvedBy: release.approved_by || '',
              prework: release.prework || '',
              notes: release.notes || '',
              createdAt: new Date(release.created_at),
              plannedReleaseDate: release.planned_release_date ? new Date(release.planned_release_date) : undefined,
              subItems: [], // We don't need sub-items for the dashboard view
              clientId: release.client_id,
              ownerId: release.owner_id,
            };
            
            return { client, release: processedRelease };
          })
          .filter((item): item is ClientRelease => item !== null);
        
        setReleases(processedReleases);
      } catch (error) {
        console.error('Unexpected error fetching releases:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReleases();
  }, [clients, toast]);

  return {
    releases,
    isLoading
  };
};
